import React, { useEffect, useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next';
import CommonGrid from '../../CommonGrid';
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../../../assets/css/custom-ag-grid.css";
import { AddWorkflowSessionValuesModal1 } from '../HttpCustomHeaders/AddWorkflowSessionValuesModal1';
import ErrorModal from '../../../ItemProperties/Forms/FieldDataValidation/ErrorModal';
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

export const MqSeriesCustomHeaders = forwardRef(({ customHeaders, setCustomHeaders, onSave }, ref) => {
    const { t } = useTranslation();
    const [deletedKeys, setDeletedKeys] = useState([]);
    const [isApplyButtonEnabled, setIsApplyButtonEnabled] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [currentRow, setCurrentRow] = useState('');
    const [modalOpen, setModalOpen] = useState({ open: false, isFirstTime: false });
    const [modalStates, setModalStates] = useState({ openModal: false, type: 0 });
    const [keyValue, setKeyValue] = useState('');
    const [errorModal, setErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [gridApi, setGridApi] = useState(null);

    const isEditable = (params) => {
        return params.data.key;
    };

    const getProcessedData = () => {
        const updated = [];
        gridApi?.forEachNode((node) => {
            const key = node?.data?.key;
            const value = node?.data?.value;
            updated.push({ key, value })
        });
        return reStructure(updated, true);
    }

    const gridOptions = {
        columnDefs: [
            {
                showDeleteIconOnly: true,
                isDeleteControl: true,
                editable: false,
                deleteHandler: (data) => {
                    setIsApplyButtonEnabled(true);
                    if (data?.initalData) {
                        setDeletedKeys(prev => [...prev, data]);
                    }
                },
                showDeleteIcon: (data) => {
                    return data.key || data.value;
                },
                headerClass: "first-column-header",
            },
            {
                headerName: t("LBL_KEY"),
                field: "key",
                editable: true,
                resizable: false,
                preventTabAndEnter: true,
            },
            {
                headerName: t("LBL_FIELD_VALUE"),
                field: "value",
                editable: isEditable,
                resizable: false,
            },
            {
                showDeleteIconOnly: true,
                cellRenderer: (info) => CustomCellRenderer(info),
                editable: isEditable,
            },
        ],
    };

    const getCurrentRow = (data) => {
        setCurrentRow(data);
    }

    const getGridAPI = (api) => {
        setGridApi(api);
    }

    const CustomCellRenderer = (info) => {
        const { data } = info;
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                    size="small"
                    disabled={!data?.key}
                    onClick={() => {
                        getCurrentRow(data);
                        setModalOpen({ open: true, isFirstTime: true });
                    }}
                    style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <MoreHorizIcon fontSize="small" />
                </button>
            </div>
        );
    };

    const reStructure = (rows, isReturn) => {
        let res = '';
        const tempKeyValues = [];
        rows?.forEach((row) => {
            tempKeyValues.push(`${row?.key}\u001f${row?.value}`);
        });
        if (tempKeyValues?.length) {
            res = tempKeyValues.join('\u001e');
        }
        setCustomHeaders(res);
        if (isReturn) return res;
    }

    const deStructureHeadersData = () => {
        const rows = [];
        if (customHeaders) {
            // Split the data into key-value pairs using record separator (\u001e)
            const keyValuePairs = customHeaders?.split("\u001e");

            // Process each key-value pair
            keyValuePairs.forEach((keyValue, index) => {
                // Split each key-value pair using unit separator (\u001f)
                const [key, value] = keyValue.split("\u001f");

                // Ensure there are at least two parts (key and value)
                if (key && value) {
                    // Process the key and value as needed
                    rows.push({ id: index + 1, key: key, value: value });
                }
            });
            rows?.push({ id: rows.length + 1, key: '', value: '' });
        } else {
            rows.push({ id: 1, key: '', value: '' });
        }
        setRowData(rows);
    }

    const checkDuplicateKey = (id, value, gridApi) => {
        if (!value) return false;
        let duplicateFound = false;
        gridApi.forEachNode((node) => {
            if (node.data.id !== id && node.data.key === value) duplicateFound = true;
        });
        return duplicateFound;
    };

    const checkEmptyKey = (data, oldValue) => {
        if ((data.inititlaData && !data.key) || (!data.key && data.value)) return { isEmptyKey: true, valueToKeep: oldValue }
        return { isEmptyKey: false }
    }

    const updateRowData = (updatedHeaders) => {
        setRowData(updatedHeaders);
        reStructure(updatedHeaders);
    }

    const setFinalValue = (value) => {
        setKeyValue(value);
        const updatedHeaders = rowData?.map((row) =>
            currentRow?.id === row?.id ? { ...row, value: value } : row
        );
        updateRowData(updatedHeaders)
    };

    const handleCellValueChanged = (gridOptions) => {
        const data = gridOptions?.data;
        const { value, key, id } = data;
        if (gridOptions.newValue !== gridOptions.oldValue) setIsApplyButtonEnabled(true);
        const colId = gridOptions.column.colId;
        if (colId === "key") {
            const id = gridOptions.data.id;
            const newValue = gridOptions.data.key;
            const rowIndex = gridOptions.rowIndex;
            const isDuplicateKey = checkDuplicateKey(id, newValue, gridOptions.api);
            const { isEmptyKey, valueToKeep } = checkEmptyKey(gridOptions.data, gridOptions.oldValue);
            if (isDuplicateKey) {
                var rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
                setErrorModal(true);
                setErrorMessage(t("BOX_NAME_ALREADY_EXISTS"));
                rowNode.setDataValue(colId, gridOptions?.oldValue || "");
            }
            else if (isEmptyKey) {
                var rowNode = gridOptions.api.getDisplayedRowAtIndex(rowIndex);
                setErrorModal(true);
                setErrorMessage(t("BOX_ENTER_NEW_NM_FOR_KEY"));
                if (valueToKeep) rowNode.setDataValue(colId, valueToKeep);
            } else {
                const updatedHeaders = rowData?.map((row) => id === row?.id ? { ...row, key: key, value: value } : row);
                if (key && id === updatedHeaders.length) updatedHeaders?.push({ id: updatedHeaders?.length + 1, key: '', value: '' })
                updateRowData(updatedHeaders)
            }
        }
    };

    useEffect(() => {
        deStructureHeadersData();
    }, [])

    useImperativeHandle(ref, () => ({
        getProcessedData
    }));

    return (
        <>
            <div style={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, overflow: 'hidden' }}>
                    <CommonGrid
                        rowData={rowData}
                        gridOptions={gridOptions}
                        getGridAPI={getGridAPI}
                        gridProps={{
                            onCellValueChanged: handleCellValueChanged
                        }}
                        containerClasses={'account-data-grid-container'}
                    />
                </div>
            </div>
            <ErrorModal
                errorModal={errorModal}
                errorMessage={errorMessage}
                handleErrorModal={setErrorModal}
            />
            {modalOpen && <AddWorkflowSessionValuesModal1
                setModalOpen={setModalOpen}
                modalStates={modalStates}
                setModalStates={setModalStates}
                modalOpen={modalOpen}
                handleClose={() => setModalOpen({ open: false, isFirstTime: false })}
                finalValue={keyValue}
                currentRowKey={currentRow?.key}
                currentValue={currentRow?.value}
                setFinalValue={setFinalValue}
            />}
        </>
    )
})

export default MqSeriesCustomHeaders 